import React, { useState } from "react";
import {
  Box,
  FormControl,
  FormLabel,
  Typography,
  Grid,
  Divider,
} from "@mui/material";
import ArchitectureIcon from "@mui/icons-material/Architecture";
import StraightenIcon from "@mui/icons-material/Straighten";
import {
  Button,
  ButtonColorOption,
  ButtonVariantOption,
} from "@/components/Button";
import type { Layer } from "leaflet";

interface IPopupContent {
  shapeArea: string;
  shapeRadius?: string;
  layer: Layer;
  searchSelectedArea: (layer: Layer) => Promise<void>;
  drawnItemsRef: React.MutableRefObject<L.FeatureGroup<any>>;
  hideSearchButton?: boolean;
}

/**
 * 地図上に表示する面積ポップアップを表示するReactコンポーネント
 * @param props shapeArea 面積、無加工でそのまま表示されるので単位を表示したい場合は単位込みの文字列を渡す
 * @param props shapeRadius 半径、表示しない場合はundefined
 * @param props layer 選択している範囲を表すレイヤー
 * @param props handleDrawCreatedReal 検索処理を実行する関数
 * @param props drawnItemsRef 範囲を削除する時に使用するrefs
 */
export const AreaSelectPopupContent: React.FC<IPopupContent> = (props) => {
  const {
    shapeArea,
    shapeRadius,
    layer,
    searchSelectedArea,
    drawnItemsRef,
    hideSearchButton,
  }: IPopupContent = props;

  // 検索リクエスト中はボタンを無効化し、検索ボタンにローディングを表示する
  const [isSearching, setIsSearching] = useState(false);

  const handleSearch = async (): Promise<void> => {
    setIsSearching(true);
    try {
      await searchSelectedArea(layer);
    } finally {
      setIsSearching(false);
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      <FormControl sx={{ width: "100%" }}>
        <FormLabel>
          <ArchitectureIcon fontSize={"inherit"} />
          面積
        </FormLabel>
        <Typography
          ml={2}
          my={1}
          fontSize={"1.2rem"}
          variant={"body1"}
          component={"span"}
        >
          {shapeArea}
        </Typography>
        {shapeRadius && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <FormLabel>
              <StraightenIcon fontSize={"inherit"} />
              半径
            </FormLabel>
            <Typography
              ml={2}
              my={1}
              fontSize={"1.2rem"}
              variant={"body1"}
              component={"span"}
            >
              {shapeRadius}
            </Typography>
          </>
        )}
      </FormControl>
      <Divider />
      <Box mt={2}>
        <Grid container spacing={2}>
          {hideSearchButton ? null : (
            <Grid item xs={12}>
              <Button
                label={"この範囲で検索する"}
                onClick={() => {
                  void handleSearch();
                }}
                variant={ButtonVariantOption.Contained}
                hasLoading={true}
                loading={isSearching}
                disabled={isSearching}
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <Button
              label={"この範囲を削除する"}
              onClick={() => {
                drawnItemsRef.current.removeLayer(layer);
              }}
              color={ButtonColorOption.Error}
              variant={ButtonVariantOption.Contained}
              disabled={isSearching}
            />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};
